/**
 * "Remove Member" for the combined overview's per-tile ✕ (COMBINED-V2-DATA-MAP
 * §7.3; detach model 2026-07-13). Removal DETACHES the member from the
 * household — `removeMember` intent sets `group_id` → NULL, nothing is ever
 * deleted; the application continues as its own standalone application on the
 * property. The WHY sets what else is recorded (proposal §5 exits table):
 *  - rejected → status Declined stamped in the same write (terminal, visible)
 *  - left voluntarily → status Withdrawn (terminal, visible)
 *  - added by mistake → detach only, nothing recorded (also the ONLY option
 *    for an already-decided member — decisions are final)
 * Gated upstream on `can.removeMember` (`applications.merge` for detach,
 * or reject/withdraw for decision exits) + the household decision being open.
 */
import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { useFetcher } from 'react-router'
import type {
	DetailActionResult,
	GroupMember,
} from '#app/utils/application-detail-v2/types'
import { isDecisionLocked } from '#app/utils/landlord-application-actions'
import { cn } from '#app/utils/misc'
import {
	ActionResultMarker,
	DetailDialog,
	InlineActionError,
	PillButton,
} from '../dialogs/dialog-bits'

export type RemovalChoice = 'declined' | 'withdrawn' | 'mistake'

const REMOVAL_OPTIONS: Array<{
	value: RemovalChoice
	label: string
	hint: string
}> = [
	{
		value: 'declined',
		label: 'Declined by you',
		hint: 'You rejected this profile — marked Declined and removed from the household; stays visible as its own application.',
	},
	{
		value: 'withdrawn',
		label: 'Left voluntarily',
		hint: 'The member stepped out on their own — marked Withdrawn and removed from the household; stays visible as its own application.',
	},
	{
		value: 'mistake',
		label: 'Added by mistake',
		hint: 'A correction, not a decision — removed from this household with NOTHING recorded; their application simply continues separately.',
	},
]

export function RemoveMemberDialog({
	open,
	onOpenChange,
	member,
	onRemoved,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** The member targeted by the ✕; null while the dialog is closed. */
	member: GroupMember | null
	/** Fired after a successful removal (before the dialog closes) — the
	 * controller navigates away when the ENTRY application detached itself. */
	onRemoved?: (member: GroupMember, choice: RemovalChoice) => void
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const busy = fetcher.state !== 'idle'
	const name = member?.displayName ?? 'this member'
	const terminal = member ? isDecisionLocked(member.status) : false

	// An already-decided member can't be re-decided (finality) — only the
	// detach-as-mistake remains; the disabled rows say why.
	const decidedBlockedReason = terminal
		? `Already ${member?.status} — decisions are final, they can only be removed from the household.`
		: null

	const options = REMOVAL_OPTIONS.map((option) => ({
		...option,
		disabled: option.value !== 'mistake' && decidedBlockedReason != null,
		hint:
			option.value !== 'mistake' && decidedBlockedReason
				? decidedBlockedReason
				: option.hint,
	}))
	const defaultChoice: RemovalChoice = terminal ? 'mistake' : 'declined'
	const [choice, setChoice] = useState<RemovalChoice>(defaultChoice)
	const [note, setNote] = useState('')
	// The dialog can open for different members in one mount — re-seed the
	// selection whenever the target changes (render-time state adjustment,
	// no useEffect).
	const [seededFor, setSeededFor] = useState<string | null>(null)
	if (member && seededFor !== member.applicationId) {
		setSeededFor(member.applicationId)
		setChoice(defaultChoice)
		setNote('')
	}

	const resetState = () => {
		setChoice(defaultChoice)
		setNote('')
		setSeededFor(null)
	}
	const selected = options.find((option) => option.value === choice)
	const submitBlocked = !selected || selected.disabled

	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback="Member removed"
				onSuccess={() => {
					if (member) onRemoved?.(member, choice)
					resetState()
					onOpenChange(false)
				}}
			/>
			<DetailDialog
				open={open}
				onOpenChange={(next) => {
					onOpenChange(next)
					if (!next) resetState()
				}}
				title={`Remove ${name} from this household?`}
				description="Removes them from this household (unmerge) — their application is never deleted; it continues on its own. Decline or voluntary exit is also recorded on their profile; a mistake removal records nothing."
				className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-xl"
			>
				<fieldset className="flex flex-col gap-2" disabled={busy}>
					<legend className="pb-1 text-sm font-medium text-[#32302F]">
						Why is {name} leaving?
					</legend>
					{options.map((option) => (
						<label
							key={option.value}
							className={cn(
								'flex items-start gap-3 rounded-xl border p-3 transition-colors',
								option.disabled
									? 'cursor-not-allowed border-[#F0F0F0] opacity-60'
									: 'cursor-pointer',
								!option.disabled &&
									(choice === option.value
										? 'border-[#32302F] bg-[#FBFDFD]'
										: 'border-[#F0F0F0] hover:border-[#CCCCCC]'),
							)}
						>
							<input
								type="radio"
								name="removalChoice"
								value={option.value}
								checked={choice === option.value}
								disabled={option.disabled}
								onChange={() => setChoice(option.value)}
								className="mt-1 accent-[#32302F]"
							/>
							<span className="min-w-0">
								<span className="block text-sm font-medium text-[#32302F]">
									{option.label}
								</span>
								<span className="block text-xs text-[#747474]">
									{option.hint}
								</span>
							</span>
						</label>
					))}
				</fieldset>

				{/* The mistake removal records nothing, so there is nothing to note. */}
				{choice !== 'mistake' ? (
					<div className="flex flex-col gap-2">
						<label
							htmlFor="remove-member-note"
							className="text-sm font-medium text-[#32302F]"
						>
							Note (optional)
						</label>
						<textarea
							id="remove-member-note"
							rows={2}
							value={note}
							onChange={(event) => setNote(event.target.value)}
							disabled={busy}
							placeholder="e.g., Household continuing without this member..."
							className="w-full resize-none rounded-xl border border-[#F0F0F0] px-4 py-3 text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC] focus:border-[#32302F]"
						/>
					</div>
				) : null}

				<InlineActionError fetcher={fetcher} open={open} />
				<div className="flex justify-end gap-3">
					<PillButton
						tone="outline"
						onClick={() => onOpenChange(false)}
						disabled={busy}
					>
						Cancel
					</PillButton>
					<PillButton
						tone="danger"
						disabled={busy || !member || submitBlocked}
						onClick={() => {
							if (!member) return
							const form: Record<string, string> = {
								intent: 'removeMember',
								applicationId: member.applicationId,
								removalStatus: choice,
							}
							if (choice !== 'mistake' && note.trim()) {
								form.statusNote = note.trim()
							}
							void fetcher.submit(form, { method: 'post' })
						}}
					>
						{busy
							? 'Removing...'
							: choice === 'declined'
								? `Decline ${name}`
								: choice === 'withdrawn'
									? `Withdraw ${name}`
									: `Remove ${name}`}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</DetailDialog>
		</>
	)
}
